// Ahmed 1 Nov: cancel a wrong payment, from a row of the PO's Payments card. The reason is required. It stays on the card, struck through,
// and stops counting on every PO it covered. The window says what else follows (a discount back to "pay less", a bank line back to explain,
// a week already handed in) and when it cannot be done (finance paid some of it, or its bank line is in a week handed in).
import React, { useEffect, useState } from 'react';
import { Alert, Input, Modal, Spin, message } from 'antd';
import dayjs from 'dayjs';
import { cancelPayment, getPaymentDetail, PaymentDetail } from '../../api';
import { money } from '../receiving/shared';

const CancelPaymentWindow: React.FC<{ paymentId: number | null; onClose: () => void; onSaved: () => void }> = ({ paymentId, onClose, onSaved }) => {
  const [detail, setDetail] = useState<PaymentDetail | null>(null);
  const [why, setWhy] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setDetail(null); setWhy('');
    if (!paymentId) return;
    getPaymentDetail(paymentId).then(setDetail).catch((e) => { message.error(e?.response?.data?.error || 'Could not open the payment'); onClose(); });
  }, [paymentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const p = detail?.payment;
  const covered = p?.credit?.covered || 0;
  const frozen = detail?.bankLine?.frozen || null;
  const refused = covered > 0.005 || !!frozen;
  const week = p ? detail?.handedWeeks.find((w) => w.from <= p.paidOn && p.paidOn <= w.to) : undefined;
  const pos = p?.pos || [];

  const save = async () => {
    if (!paymentId) return;
    setSaving(true);
    try {
      const res = await cancelPayment(paymentId, why.trim());
      message.success(`${res.code} cancelled`);
      onSaved();
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Could not cancel the payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={!!paymentId}
      title={p ? `Cancel ${p.code}` : 'Cancel the payment'}
      onCancel={onClose}
      onOk={save}
      okText="Cancel this payment"
      cancelText="Keep it"
      okButtonProps={{ danger: true, disabled: !detail || refused || !why.trim(), loading: saving }}
      maskClosable={false}
      destroyOnClose
    >
      {!p ? <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 13.5 }}>
            <b>EGP {money(p.amount)}</b> · {p.methodLabel} · {dayjs(p.paidOn).format('D MMM YYYY')}
            <div style={{ color: '#5f6672', marginTop: 2 }}>It stops counting on {pos.length > 1 ? `${pos.length} POs: ${pos.join(', ')}` : pos[0]}. It stays on the PO, struck through, with your reason.</div>
          </div>
          {covered > 0.005 && <Alert type="error" showIcon message={<span style={{ fontSize: 13 }}>Finance already paid EGP {money(covered)} of this purchase on credit, so it cannot be cancelled. Finance can reverse its payment first{p.credit?.by.length ? ` (${p.credit.by.map((x) => x.code).join(', ')})` : ''}.</span>} />}
          {frozen && <Alert type="error" showIcon message={<span style={{ fontSize: 13 }}>It is paired with the ADIB line of {dayjs(detail!.bankLine!.on).format('D MMM')}, in a week already handed in to <bdi>{frozen.handedTo}</bdi>, and that week cannot change. It cannot be cancelled; its POs and lines can still be corrected.</span>} />}
          {!refused && detail!.bankLine && <Alert type="warning" showIcon message={<span style={{ fontSize: 13 }}>The ADIB line of {dayjs(detail!.bankLine.on).format('D MMM')} (EGP {money(detail!.bankLine.debit)}) goes back to explain on Purchasing account.</span>} />}
          {!refused && detail!.discounts.length > 0 && <Alert type="warning" showIcon message={<span style={{ fontSize: 13 }}>{detail!.discounts.map((x) => `The EGP ${money(x.amount)} discount on ${x.poId} line ${x.lineNo}`).join(', ')} goes back to "pay less".</span>} />}
          {!refused && week && <Alert type="warning" showIcon message={<span style={{ fontSize: 13 }}><b>That week was already handed in to <bdi>{week.handedTo}</bdi>.</b> This saves, but the handed-in sheet does not change, so tell them.</span>} />}
          {!refused && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: '#5f6672' }}>
              <span><span style={{ color: '#cf1322' }}>* </span>Why is it cancelled?</span>
              <Input.TextArea value={why} onChange={(e) => setWhy(e.target.value)} rows={2} maxLength={500} placeholder="e.g. recorded on the wrong PO" autoFocus />
            </label>
          )}
        </div>
      )}
    </Modal>
  );
};

export default CancelPaymentWindow;
